import React, { useCallback, useMemo } from 'react'

import type {
  BinaryFilter,
  Filter,
  LogicalAndFilter,
  UnaryFilter,
} from '@cubejs-client/core'
import { useISOCountryData } from 'cube/components/CountryCodeSelect/useISOCountryData.hook'
import {
  CubeReportPage,
  REPORT_TYPE_TO_FILTER_MAP,
} from 'cube/components/CubeReportFilterContainer'
import {
  ConversationParticipantStatus,
  CubeFilterDimensions,
  CubeOperator,
  dayOfWeekOptions,
  endUsersCompanyNameFilterOptionsQuery,
  endUsersEmailFilterOptionsQuery,
  formatBaseModelTopicsOptionsQuery,
  formatConversationStartedFromUrlQuery,
  formatConversationTagOptionsQuery,
  formatInboxOptionsQuery,
  formatLeadStageOptionsQuery,
  formatPlaybookFilterOptionsQuery,
  formatTeamFilterOptionsQuery,
  formatTeammatesOptionsQuery,
  formatTopicFilterOptionsQuery,
  formatTopicsPerformanceExcludedTopicsQuery,
  gptEnabledPlaybooksOptionsQuery,
  GroupNames,
  HumanizedCubeFilterDimensions,
  HumanizedCubeOperators,
  isCubeFilterKeyValueType,
  meetingOutcomesOptions,
  operatorHasNumericInput,
  operatorHasTextInput,
  opportunitiesOptions,
  opportunityStageOptions,
  opportunityTypeOptions,
  routingOutcomeOptions,
} from 'cube/components/CubeReportFilterRow'
import { formatHourOfDayOptions } from 'cube/components/HourRangeSelect'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  DRIFT_CHAT_APP_USERS_DIMENSIONS,
  DRIFT_CHAT_CLASSIFICATIONS_DIMENSIONS,
  DRIFT_CHAT_END_USERS_DIMENSIONS,
  DRIFT_CHAT_INBOX_DIMENSIONS,
  DRIFT_CHAT_OPPORTUNITY_OVERVIEW,
  DRIFT_CHAT_PLAYBOOK_DIMENSIONS,
  DRIFT_CHAT_TAGS_DIMENSIONS,
  DRIFT_CHAT_TEAMS_DIMENSIONS,
  DRIFT_CHAT_TOPIC_PREDICTIONS,
  DRIFT_CHAT_TOPICS,
  DRIFT_CHAT_TOPICS_PERFORMANCE_EXCLUDED_TOPICS,
} from 'cube/constants'
import { useCubeFilterOptionsList } from 'cube/hooks/useCubeFilterOptionsList.hook'
import { equals, path, pickBy } from 'ramda'

import { type SelectOption, FeatureBadge } from '@driftt/dds'

import { CHAT_REPORT_TYPES } from 'constants/playbooks'
import { useConversationDetailStore } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.context'
import { hasGates } from 'utils/access'
import { isNilOrEmpty } from 'utils/ramda'

import useExposedCategories, {
  ICategoryOption,
} from '../CategoriesOptionsList.hook'
import { TEMPORAL_AVAILABLE_LANGUAGES } from '../LanguageCodeSelect/constants'
import {
  IScenarioOption,
  useExposedScenarios,
} from '../ScenariosOptionsList.hook'
import useSkillData from '../SkillTypeSelect.hook'

export type HumanizedFilter = {
  member: { value: string; label: string }
  operator: string
  values: string[]
  filter: Filter
}

export interface IFilterableCubeEntity {
  member: {
    value: CubeFilterDimensions
    label: string
    prefix?: React.ReactNode
  }
  /**
   * Available cube operators. First operator is the default.
   */
  availableOperations: CubeOperator[]
  optionList: SelectOption[]
  enforceSingleSelect?: boolean
  group?: GroupNames
  gate?: string
}

export type GroupedFilter = {
  label: string
  options: IFilterableCubeEntity[]
}

export type FilterableEntityMap = Record<
  CubeFilterDimensions,
  IFilterableCubeEntity
>

interface cubeReportFilterableEntitiesResult {
  filterableEntitiesByReportType: FilterableEntityMap
  getHumanizedEntityByFilter: (
    filter: BinaryFilter | UnaryFilter
  ) => HumanizedFilter | null
}

const BOOLEAN_FILTER_VALUES: SelectOption<string>[] = [
  {
    label: 'true',
    value: 'true',
  },
  {
    label: 'false',
    value: 'false',
  },
]

const isHourOfDayFilter = (filter: Filter) =>
  equals(CubeFilterDimensions.HOUR_OF_DAY, path(['member'], filter))

const isWordsOrPhrasesFilter = (filter: Filter) =>
  equals(
    CubeFilterDimensions.CHAT_INTERACTIONS_MESSAGE_BODY,
    path(['member'], filter)
  )
const isLanguageCodeFilter = (filter: Filter) =>
  equals(CubeFilterDimensions.LANGUAGE_DETECTED, path(['member'], filter))
const isCountryCodeFilter = (filter: Filter) =>
  equals(CubeFilterDimensions.END_USER_COUNTRY_CODE, path(['member'], filter))
const isPlaybookTypeFilter = (filter: Filter) =>
  equals(CubeFilterDimensions.PLAYBOOK_TYPE, path(['member'], filter))
/**
 * Hook that manages cube filter config. Results are filtered by report type.
 *
 * @param reportType CubeReportPage
 * @returns { FilterableEntityMap } filterableEntitiesByReportType
 */
export const useCubeReportFilterableEntities = (
  reportType: CubeReportPage
): cubeReportFilterableEntitiesResult => {
  const { countryOptions } = useISOCountryData()
  const { skillTypeOptions } = useSkillData()
  const exposedScenarioOptions: IScenarioOption[] = useExposedScenarios()

  const exposedCategoryOptions: ICategoryOption[] = useExposedCategories()

  const {
    store: { startDate, endDate },
  } = useConversationDetailStore()

  const { optionsList: playbookOptionsList } = useCubeFilterOptionsList(
    formatPlaybookFilterOptionsQuery(startDate, endDate),
    DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_NAME,
    CONVERSATION_FACTS_DIMENSIONS.PLAYBOOK_ID
  )

  const { optionsList: teamOptionsList } = useCubeFilterOptionsList(
    formatTeamFilterOptionsQuery(),
    DRIFT_CHAT_TEAMS_DIMENSIONS.TEAM_NAME,
    DRIFT_CHAT_TEAMS_DIMENSIONS.TEAM_ID
  )

  const { optionsList: leadStageOptions } = useCubeFilterOptionsList(
    formatLeadStageOptionsQuery(),
    DRIFT_CHAT_CLASSIFICATIONS_DIMENSIONS.CLASSIFICATION_NAME,
    CONVERSATION_FACTS_DIMENSIONS.LAST_CLASSIFICATION_ID
  )

  const { optionsList: teammatesOptions } = useCubeFilterOptionsList(
    formatTeammatesOptionsQuery(),
    DRIFT_CHAT_APP_USERS_DIMENSIONS.DISPLAY_NAME,
    DRIFT_CHAT_APP_USERS_DIMENSIONS.USER_ID
  )

  const { optionsList: inboxOptions } = useCubeFilterOptionsList(
    formatInboxOptionsQuery(),
    DRIFT_CHAT_INBOX_DIMENSIONS.NAME,
    DRIFT_CHAT_INBOX_DIMENSIONS.ID
  )

  const { optionsList: tagOptions } = useCubeFilterOptionsList(
    formatConversationTagOptionsQuery(),
    DRIFT_CHAT_TAGS_DIMENSIONS.TAG_NAME,
    DRIFT_CHAT_TAGS_DIMENSIONS.TAG_NAME
  )

  const { optionsList: convoStartedFromOptions } = useCubeFilterOptionsList(
    formatConversationStartedFromUrlQuery(),
    CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_FROM_URL,
    CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_FROM_URL
  )

  const { optionsList: endUsersEmailOptionsList } = useCubeFilterOptionsList(
    endUsersEmailFilterOptionsQuery(startDate, endDate),
    DRIFT_CHAT_END_USERS_DIMENSIONS.EMAIL,
    DRIFT_CHAT_END_USERS_DIMENSIONS.EMAIL
  )

  const { optionsList: endUsersCompanyNameOptionsList } =
    useCubeFilterOptionsList(
      endUsersCompanyNameFilterOptionsQuery(startDate, endDate),
      DRIFT_CHAT_END_USERS_DIMENSIONS.COMPANY_NAME,
      DRIFT_CHAT_END_USERS_DIMENSIONS.COMPANY_NAME
    )

  const { optionsList: AITopicOptionsList } = useCubeFilterOptionsList(
    formatTopicFilterOptionsQuery(),
    DRIFT_CHAT_TOPICS.TOPIC_NAME,
    DRIFT_CHAT_TOPICS.TOPIC_ID
  )

  const { optionsList: topicsPerformanceExcludedTopicsList } =
    useCubeFilterOptionsList(
      formatTopicsPerformanceExcludedTopicsQuery(),
      DRIFT_CHAT_TOPICS_PERFORMANCE_EXCLUDED_TOPICS.TOPIC_NAME,
      DRIFT_CHAT_TOPICS_PERFORMANCE_EXCLUDED_TOPICS.TOPIC_ID
    )

  const { optionsList: opportunityStageOptionsList } = useCubeFilterOptionsList(
    opportunityStageOptions(),
    DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_STAGE,
    DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_STAGE
  )

  const { optionsList: opportunityTypeOptionsList } = useCubeFilterOptionsList(
    opportunityTypeOptions(),
    DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_TYPE,
    DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_TYPE
  )
  const { optionsList: BaseModelTopicOptionsList } = useCubeFilterOptionsList(
    formatBaseModelTopicsOptionsQuery(),
    DRIFT_CHAT_TOPIC_PREDICTIONS.TOPIC_LABEL,
    DRIFT_CHAT_TOPIC_PREDICTIONS.TOPIC_ID
  )

  const { optionsList: opportunityOptionsList } = useCubeFilterOptionsList(
    opportunitiesOptions(startDate, endDate),
    DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_NAME,
    DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_NAME
  )

  const chatPlaybooksOptionsList = useMemo(
    () => [{ label: 'Chat playbooks', value: CHAT_REPORT_TYPES }],
    []
  )

  const { optionsList: gptEnabledPlaybooksOptionsList } =
    useCubeFilterOptionsList(
      gptEnabledPlaybooksOptionsQuery(startDate, endDate),
      DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_NAME,
      DRIFT_CHAT_PLAYBOOK_DIMENSIONS.PLAYBOOK_ID
    )

  const allFilterEntities: FilterableEntityMap = useMemo(
    () => ({
      [CubeFilterDimensions.CONVERSATION_HAS_UNRESPONSIVE_FALLBACK]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_HAS_UNRESPONSIVE_FALLBACK,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_HAS_UNRESPONSIVE_FALLBACK
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.CHAT_INTERACTIONS_MESSAGE_BODY]: {
        member: {
          value: CubeFilterDimensions.CHAT_INTERACTIONS_MESSAGE_BODY,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CHAT_INTERACTIONS_MESSAGE_BODY
            ],
        },
        availableOperations: [CubeOperator.CONTAINS],
        optionList: [], // Words or Phrases options are handled in WordsOrPhrasesSelect
        group: GroupNames.CHAT_DETAILS,
      },
      [CubeFilterDimensions.CONVERSATION_PLAYBOOK_ID]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_PLAYBOOK_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_PLAYBOOK_ID
            ],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: playbookOptionsList,
        group: GroupNames.PLAYBOOKS,
      },
      [CubeFilterDimensions.LANGUAGE_DETECTED]: {
        member: {
          value: CubeFilterDimensions.LANGUAGE_DETECTED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.LANGUAGE_DETECTED
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: [], // language options are handled in LanguageCodeSelect
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.END_USER_COUNTRY_CODE]: {
        member: {
          value: CubeFilterDimensions.END_USER_COUNTRY_CODE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.END_USER_COUNTRY_CODE
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: [], // geo country options are handled in CountryCodeSelect
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.IS_END_USER_MOBILE]: {
        member: {
          value: CubeFilterDimensions.IS_END_USER_MOBILE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.IS_END_USER_MOBILE
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.CONVERSATION_PARTICIPANT_STATUS]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_PARTICIPANT_STATUS,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_PARTICIPANT_STATUS
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: [
          {
            label: 'Teammate only',
            value: ConversationParticipantStatus.PERSON_ONLY,
          },
          {
            label: 'Bot only',
            value: ConversationParticipantStatus.BOT_ONLY,
          },
          {
            label: 'Teammate and bot',
            value: ConversationParticipantStatus.MIXED,
          },
        ],
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.TEAM_PARTICIPATED_TEAM_ID]: {
        member: {
          value: CubeFilterDimensions.TEAM_PARTICIPATED_TEAM_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.TEAM_PARTICIPATED_TEAM_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: teamOptionsList,
        group: GroupNames.LIVE_CHAT,
      },
      [CubeFilterDimensions.USER_PARTICIPATED_USER_ID]: {
        member: {
          value: CubeFilterDimensions.USER_PARTICIPATED_USER_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.USER_PARTICIPATED_USER_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: teammatesOptions,
        group: GroupNames.LIVE_CHAT,
      },
      [CubeFilterDimensions.INITIAL_ROUTED_IN_USER]: {
        member: {
          value: CubeFilterDimensions.INITIAL_ROUTED_IN_USER,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.INITIAL_ROUTED_IN_USER
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: teammatesOptions,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.INITIAL_ROUTED_IN_TEAM_ID]: {
        member: {
          value: CubeFilterDimensions.INITIAL_ROUTED_IN_TEAM_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.INITIAL_ROUTED_IN_TEAM_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: teamOptionsList,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.CONVERSATION_CLASSIFICATION_ID]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_CLASSIFICATION_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_CLASSIFICATION_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: leadStageOptions,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.CONVERSATION_INBOX_ID]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_INBOX_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_INBOX_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: inboxOptions,
        group: GroupNames.CHAT_DETAILS,
      },
      [CubeFilterDimensions.DAY_OF_WEEK]: {
        member: {
          value: CubeFilterDimensions.DAY_OF_WEEK,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.DAY_OF_WEEK],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: dayOfWeekOptions,
        group: GroupNames.CHAT_DETAILS,
      },
      [CubeFilterDimensions.HOUR_OF_DAY]: {
        member: {
          value: CubeFilterDimensions.HOUR_OF_DAY,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.HOUR_OF_DAY],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: [], // hour of day options are handled in HourOfDaySelect
        group: GroupNames.CHAT_DETAILS,
      },
      [CubeFilterDimensions.BUYER_SENTIMENT]: {
        member: {
          value: CubeFilterDimensions.BUYER_SENTIMENT,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.BUYER_SENTIMENT],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: [
          { label: 'Positive', value: 'POS' },
          { label: 'Negative', value: 'NEG' },
          { label: 'Neutral', value: 'NEU' },
        ],
        group: GroupNames.CHAT_DETAILS,
        gate: 'has_buyer_sentiment_reporting',
      },
      [CubeFilterDimensions.CONVERSATION_TAG_NAME]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_TAG_NAME,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_TAG_NAME
            ],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: tagOptions,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.CQL_SCORE]: {
        member: {
          value: CubeFilterDimensions.CQL_SCORE,
          label: HumanizedCubeFilterDimensions[CubeFilterDimensions.CQL_SCORE],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: [
          { label: '-1', value: '-1' },
          { label: '1', value: '1' },
          { label: '2', value: '2' },
          { label: '3', value: '3' },
        ],
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: routingOutcomeOptions,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.TEAMMATES_UNAVAILABLE]: {
        member: {
          value: CubeFilterDimensions.TEAMMATES_UNAVAILABLE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.TEAMMATES_UNAVAILABLE
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.CONVERSATION_STARTED_FROM_URL]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_STARTED_FROM_URL,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_STARTED_FROM_URL
            ],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.CONTAINS,
          CubeOperator.DOES_NOT_CONTAIN,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: convoStartedFromOptions,
        group: GroupNames.CHAT_DETAILS,
      },
      [CubeFilterDimensions.TEAMMATE_RESPONDED]: {
        member: {
          value: CubeFilterDimensions.TEAMMATE_RESPONDED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.TEAMMATE_RESPONDED
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.TEAMMATE_ROUTED_IN_TO_RESPONDED_WITHIN60S]: {
        member: {
          value: CubeFilterDimensions.TEAMMATE_ROUTED_IN_TO_RESPONDED_WITHIN60S,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.TEAMMATE_ROUTED_IN_TO_RESPONDED_WITHIN60S
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.IS_DEAD_END_CONVERSATION]: {
        member: {
          value: CubeFilterDimensions.IS_DEAD_END_CONVERSATION,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.IS_DEAD_END_CONVERSATION
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.TEAMMATE_RESPONDED_LATE]: {
        member: {
          value: CubeFilterDimensions.TEAMMATE_RESPONDED_LATE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.TEAMMATE_RESPONDED_LATE
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.EMAIL_CAPTURED]: {
        member: {
          value: CubeFilterDimensions.EMAIL_CAPTURED,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.EMAIL_CAPTURED],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.PHONE_CAPTURED]: {
        member: {
          value: CubeFilterDimensions.PHONE_CAPTURED,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.PHONE_CAPTURED],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.URL_QUERY_PARAMETERS_PARAM_KEY_VALUE]: {
        member: {
          value: CubeFilterDimensions.URL_QUERY_PARAMETERS_PARAM_KEY_VALUE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.URL_QUERY_PARAMETERS_PARAM_KEY_VALUE
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: [], // key value options are handled in KeyValueOptions
        group: GroupNames.CHAT_DETAILS,
      },
      [CubeFilterDimensions.MEETING_BOOKED]: {
        member: {
          value: CubeFilterDimensions.MEETING_BOOKED,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.MEETING_BOOKED],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.PLAYBOOK_METRICS_IS_END_USER_MOBILE]: {
        member: {
          value: CubeFilterDimensions.PLAYBOOK_METRICS_IS_END_USER_MOBILE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.PLAYBOOK_METRICS_IS_END_USER_MOBILE
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.GEO_COUNTRY_CODE]: {
        member: {
          value: CubeFilterDimensions.GEO_COUNTRY_CODE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.GEO_COUNTRY_CODE
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: [], // geo country options are handled in CountryCodeSelect
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.PLAYBOOK_TYPE]: {
        member: {
          value: CubeFilterDimensions.PLAYBOOK_TYPE,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.PLAYBOOK_TYPE],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: [],
        group: GroupNames.PLAYBOOKS,
      },
      [CubeFilterDimensions.NODE_TRIGGERED]: {
        member: {
          value: CubeFilterDimensions.NODE_TRIGGERED,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.NODE_TRIGGERED],
          prefix: <FeatureBadge variant="beta" bolt />,
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: skillTypeOptions,
        group: GroupNames.PLAYBOOKS,
      },
      [CubeFilterDimensions.SCENARIO_TRIGGERED]: {
        member: {
          value: CubeFilterDimensions.SCENARIO_TRIGGERED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.SCENARIO_TRIGGERED
            ],
          prefix: <FeatureBadge variant="beta" bolt />,
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: exposedScenarioOptions,
        group: GroupNames.PLAYBOOKS,
      },
      [CubeFilterDimensions.AI_TOPIC_TRIGGERED]: {
        member: {
          value: CubeFilterDimensions.AI_TOPIC_TRIGGERED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.AI_TOPIC_TRIGGERED
            ],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: AITopicOptionsList,
        group: GroupNames.PLAYBOOKS,
      },
      [CubeFilterDimensions.EXCLUDED_AI_TOPIC_TRIGGERED]: {
        member: {
          value: CubeFilterDimensions.EXCLUDED_AI_TOPIC_TRIGGERED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.EXCLUDED_AI_TOPIC_TRIGGERED
            ],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: topicsPerformanceExcludedTopicsList,
        group: GroupNames.PLAYBOOKS,
      },
      [CubeFilterDimensions.TOPIC_DETECTED_ID]: {
        member: {
          value: CubeFilterDimensions.TOPIC_DETECTED_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.TOPIC_DETECTED_ID
            ],
          prefix: <FeatureBadge variant="beta" bolt />,
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: BaseModelTopicOptionsList,
        group: GroupNames.PLAYBOOKS,
      },

      [CubeFilterDimensions.CONVERSATION_ROUTED]: {
        member: {
          value: CubeFilterDimensions.CONVERSATION_ROUTED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.CONVERSATION_ROUTED
            ],
        },
        availableOperations: [CubeOperator.SET, CubeOperator.NOT_SET],
        optionList: [],
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.HAS_ROUTED_AND_EVERYONE_MISSED]: {
        member: {
          value: CubeFilterDimensions.HAS_ROUTED_AND_EVERYONE_MISSED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.HAS_ROUTED_AND_EVERYONE_MISSED
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S]: {
        member: {
          value: CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S
            ],
        },
        availableOperations: [
          CubeOperator.GREATER_THAN,
          CubeOperator.LESS_THAN_OR_EQUAL,
        ],
        optionList: [],
        group: GroupNames.ROUTING,
      },
      [CubeFilterDimensions.END_USER_COMPANY_NAME]: {
        member: {
          value: CubeFilterDimensions.END_USER_COMPANY_NAME,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.END_USER_COMPANY_NAME
            ],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.CONTAINS,
          CubeOperator.DOES_NOT_CONTAIN,
        ],
        optionList: endUsersCompanyNameOptionsList,
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.END_USER_EMAIL]: {
        member: {
          value: CubeFilterDimensions.END_USER_EMAIL,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.END_USER_EMAIL],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.CONTAINS,
          CubeOperator.DOES_NOT_CONTAIN,
        ],
        optionList: endUsersEmailOptionsList,
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.HAS_CALENDAR_DROP]: {
        member: {
          value: CubeFilterDimensions.HAS_CALENDAR_DROP,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.HAS_CALENDAR_DROP
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.MEETING_STATUS]: {
        member: {
          value: CubeFilterDimensions.MEETING_STATUS,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.MEETING_STATUS],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: meetingOutcomesOptions,
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_OPPORTUNITY_TYPE]: {
        member: {
          value: CubeFilterDimensions.ROI_OPPORTUNITY_TYPE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.ROI_OPPORTUNITY_TYPE
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: opportunityTypeOptionsList,
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_OPPORTUNITY_AMOUNT]: {
        member: {
          value: CubeFilterDimensions.ROI_OPPORTUNITY_AMOUNT,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.ROI_OPPORTUNITY_AMOUNT
            ],
        },
        availableOperations: [
          CubeOperator.GREATER_THAN,
          CubeOperator.LESS_THAN_OR_EQUAL,
        ],
        optionList: [],
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_COMPANY]: {
        member: {
          value: CubeFilterDimensions.ROI_COMPANY,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.ROI_COMPANY],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.CONTAINS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: [],
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_CLOSED_DATE]: {
        member: {
          value: CubeFilterDimensions.ROI_CLOSED_DATE,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.ROI_CLOSED_DATE],
        },
        availableOperations: [CubeOperator.CONTAINS, CubeOperator.EQUALS], // TODO: Make custom date picker
        optionList: [],
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_DAYS_IN_CYCLE]: {
        member: {
          value: CubeFilterDimensions.ROI_DAYS_IN_CYCLE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.ROI_DAYS_IN_CYCLE
            ],
        },
        availableOperations: [
          CubeOperator.GREATER_THAN,
          CubeOperator.LESS_THAN_OR_EQUAL,
        ],
        optionList: [],
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_IS_INFLUENCED]: {
        member: {
          value: CubeFilterDimensions.ROI_IS_INFLUENCED,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.ROI_IS_INFLUENCED
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_IS_SOURCED]: {
        member: {
          value: CubeFilterDimensions.ROI_IS_SOURCED,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.ROI_IS_SOURCED],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_CONTACT_COUNT]: {
        member: {
          value: CubeFilterDimensions.ROI_CONTACT_COUNT,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.ROI_CONTACT_COUNT
            ],
        },
        availableOperations: [
          CubeOperator.GREATER_THAN,
          CubeOperator.LESS_THAN_OR_EQUAL,
        ],
        optionList: [],
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.ROI_OPPORTUNITY_STAGE]: {
        member: {
          value: CubeFilterDimensions.ROI_OPPORTUNITY_STAGE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.ROI_OPPORTUNITY_STAGE
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: opportunityStageOptionsList,
        group: GroupNames.PIPELINE_OUTCOMES,
      },
      [CubeFilterDimensions.CSAT_RATING]: {
        member: {
          value: CubeFilterDimensions.CSAT_RATING,
          label:
            HumanizedCubeFilterDimensions[CubeFilterDimensions.CSAT_RATING],
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: [
          { label: '1', value: '1' },
          { label: '2', value: '2' },
          { label: '3', value: '3' },
          { label: '4', value: '4' },
          { label: '5', value: '5' },
        ],
        group: GroupNames.CHAT_OUTCOMES,
      },
      [CubeFilterDimensions.OPPORTUNITY_NAME]: {
        member: {
          value: CubeFilterDimensions.OPPORTUNITY_NAME,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.OPPORTUNITY_NAME
            ],
          prefix: <FeatureBadge variant="beta" bolt />,
        },
        availableOperations: [
          CubeOperator.EQUALS,
          CubeOperator.NOT_EQUALS,
          CubeOperator.CONTAINS,
          CubeOperator.DOES_NOT_CONTAIN,
          CubeOperator.SET,
          CubeOperator.NOT_SET,
        ],
        optionList: opportunityOptionsList || [],
        group: GroupNames.OPPORTUNIES,
      },
      [CubeFilterDimensions.SITE_CONCIERGE_IS_END_USER_MOBILE]: {
        member: {
          value: CubeFilterDimensions.SITE_CONCIERGE_IS_END_USER_MOBILE,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.SITE_CONCIERGE_IS_END_USER_MOBILE
            ],
        },
        availableOperations: [CubeOperator.EQUALS],
        optionList: BOOLEAN_FILTER_VALUES,
        enforceSingleSelect: true,
        group: GroupNames.CHAT_VISITOR,
      },
      [CubeFilterDimensions.GPT_ENABLED_PLAYBOOK_ID]: {
        member: {
          value: CubeFilterDimensions.GPT_ENABLED_PLAYBOOK_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.GPT_ENABLED_PLAYBOOK_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: gptEnabledPlaybooksOptionsList,
        group: GroupNames.PLAYBOOKS,
      },
      [CubeFilterDimensions.BIONIC_MESSAGE_CATEGORY_ID]: {
        member: {
          value: CubeFilterDimensions.BIONIC_MESSAGE_CATEGORY_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.BIONIC_MESSAGE_CATEGORY_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: exposedCategoryOptions,
        group: GroupNames.CHAT_DETAILS,
      },
      [CubeFilterDimensions.BIONIC_MESSAGE_SCENARIO_ID]: {
        member: {
          value: CubeFilterDimensions.BIONIC_MESSAGE_SCENARIO_ID,
          label:
            HumanizedCubeFilterDimensions[
              CubeFilterDimensions.BIONIC_MESSAGE_SCENARIO_ID
            ],
        },
        availableOperations: [CubeOperator.EQUALS, CubeOperator.NOT_EQUALS],
        optionList: exposedScenarioOptions,
        group: GroupNames.PLAYBOOKS,
      },
    }),
    [
      playbookOptionsList,
      teamOptionsList,
      teammatesOptions,
      leadStageOptions,
      inboxOptions,
      tagOptions,
      convoStartedFromOptions,
      skillTypeOptions,
      exposedScenarioOptions,
      AITopicOptionsList,
      BaseModelTopicOptionsList,
      endUsersCompanyNameOptionsList,
      endUsersEmailOptionsList,
      opportunityTypeOptionsList,
      opportunityStageOptionsList,
      opportunityOptionsList,
      gptEnabledPlaybooksOptionsList,
      exposedCategoryOptions,
      topicsPerformanceExcludedTopicsList,
    ]
  )

  const filterableEntitiesByReportType: FilterableEntityMap = useMemo(() => {
    const availableFiltersByReportType = REPORT_TYPE_TO_FILTER_MAP[reportType]
    return pickBy((value: IFilterableCubeEntity, key) => {
      const { gate } = value
      const isGated = !isNilOrEmpty(gate)
      if (!isGated) {
        return availableFiltersByReportType.includes(key)
      } else {
        return (
          !!gate && hasGates(gate) && availableFiltersByReportType.includes(key)
        )
      }
    }, allFilterEntities)
  }, [allFilterEntities, reportType])

  const getHumanizedEntityByFilter = useCallback(
    (genericFilter: BinaryFilter | UnaryFilter | LogicalAndFilter) => {
      const { isKeyValue, keyValueFilters } =
        isCubeFilterKeyValueType(genericFilter)

      const filter: BinaryFilter = isKeyValue
        ? (keyValueFilters[0] as BinaryFilter)
        : (genericFilter as BinaryFilter)

      const filterableEntity = allFilterEntities[filter.member ?? '']

      // if value is text input, display user input instead of option label
      const isTextInput =
        operatorHasNumericInput(filter.operator as CubeOperator) ||
        operatorHasTextInput(filter.operator as CubeOperator)

      if (!filterableEntity) {
        return null
      }

      const filterOptionList = isHourOfDayFilter(filter)
        ? formatHourOfDayOptions()
        : isCountryCodeFilter(filter)
        ? countryOptions
        : isLanguageCodeFilter(filter)
        ? TEMPORAL_AVAILABLE_LANGUAGES
        : isPlaybookTypeFilter(filter)
        ? chatPlaybooksOptionsList
        : isWordsOrPhrasesFilter(filter)
        ? filter.values
        : filterableEntity.optionList

      let humanizedFilterValues = []

      if (isPlaybookTypeFilter(filter)) {
        humanizedFilterValues = filterOptionList
          .filter((filterOption) => equals(filterOption.value, filter?.values))
          .map((result) => result.label)
      } else if (isWordsOrPhrasesFilter(filter)) {
        humanizedFilterValues = filterOptionList
      } else {
        humanizedFilterValues = filterOptionList
          .filter((filterOption) =>
            filter?.values?.includes(filterOption.value)
          )
          .map((x) => x.label)
      }

      return {
        member: filterableEntity.member,
        operator: HumanizedCubeOperators[filter.operator],
        values: isTextInput ? filter.values ?? [] : humanizedFilterValues,
      } as HumanizedFilter
    },
    [allFilterEntities, countryOptions, chatPlaybooksOptionsList]
  )

  return useMemo(
    () => ({
      filterableEntitiesByReportType,
      getHumanizedEntityByFilter,
    }),
    [filterableEntitiesByReportType, getHumanizedEntityByFilter]
  )
}
